import ShaderBuilder from './shader/';
import Texture from './Texture';

import { TieupRenderer } from './renderers/TieupRenderer';
import { GridRenderer } from './renderers/GridRenderer';
import { WeaveRenderer } from './renderers/WeaveRenderer';
import { ColorRowRenderer } from './renderers/ColorRowRenderer';
import RenderStore from '../RenderStore';

import * as textureUtil from './TextureUtil';

export class Renderer {
  showTreadleOrder = true;
  showThreading = true;
  renderNextFrame = false;
  prevDraft = {};
  prevUI = {};
  dependencyGraph = {
    treadleOrder: ['treadleOrder', 'weave'],
    threading: ['threading', 'weave'],
    warpColors: ['warpColors', 'weave'],
    weftColors: ['weftColors', 'weave'],
    tieup: ['tieup', 'weave'],
    shaftCount: 'clear',
    treadleCount: 'clear',
    warpCount: ['threading', 'warpColors', 'weave'],
    weftCount: ['treadleOrder', 'weftColors', 'weave'],
    yarn: ['warpColors', 'weftColors', 'weave'],
    cellSize: 'clear',
    scrollX: ['warpColors', 'threading', 'weave'],
    scrollY: ['weftColors', 'treadleOrder', 'weave'],
  };

  constructor(canvas, scrollContainer) {
    this.scrollContainer = scrollContainer;
    this.gl = canvas.getContext('webgl', {
      premultipliedAlpha: true,
      antialias: true,
      preserveDrawingBuffer: true,
    });
    this.gl.clearColor(1, 1, 1, 1);
    this.shaders = new ShaderBuilder(this.gl);
    this.gl.disable(this.gl.DEPTH_TEST);
    this.gl.enable(this.gl.SCISSOR_TEST);
    this.startup = true;

    let maxTextureSize = this.gl.getParameter(this.gl.MAX_TEXTURE_SIZE);
    console.log('Max texture size', maxTextureSize);
    RenderStore.update((store) => ({
      ...store,
      maxTextureSize,
    }));

    this.threadingTexture = new Texture(this.gl, 1, 1, [[0, 0, 0, 0]]);
    this.treadleOrderTexture = new Texture(this.gl, 1, 1, [[0, 0, 0, 0]]);
    this.tieupTexture = new Texture(this.gl, 1, 1, [[0, 0, 0, 0]]);
    this.warpTexture = new Texture(this.gl, 1, 1, [[0, 0, 0, 0]]);
    this.weftTexture = new Texture(this.gl, 1, 1, [[0, 0, 0, 0]]);

    this.resizeCanvas();
    this.renderers = {
      tieup: {
        renderer: new TieupRenderer(this.gl, this.shaders, 'tieup'),
        dirty: true,
        enabled: true,
        updateValues: function (draft, ui) {
          this.renderer.updateValues(this.getValues(draft, ui));
        },
        getValues: (draft, ui) => ({
          xCount: draft.treadleCount,
          yCount: draft.shaftCount,
          cellSize: ui.cellSize,
          scrollX: ui.scrollX,
          scrollY: ui.scrollY,
        }),
      },
      threading: {
        renderer: new GridRenderer(
          this.gl,
          this.shaders,
          false,
          true,
          false,
          'threading'
        ),
        updateValues: function (draft, ui) {
          this.renderer.updateValues(this.getValues(draft, ui));
        },
        getValues: (draft, ui) => ({
          xCount: draft.warpCount,
          yCount: draft.shaftCount,
          weftCount: draft.weftCount,
          warpCount: draft.warpCount,
          cellSize: ui.cellSize,
          scrollX: ui.scrollX,
          scrollY: ui.scrollY,
          xStepDistance: ui.xStepDistance,
        }),
        dirty: true,
        enabled: true,
      },
      treadleOrder: {
        renderer: new GridRenderer(
          this.gl,
          this.shaders,
          true,
          false,
          true,
          'treadleOrder'
        ),
        updateValues: function (draft, ui) {
          this.renderer.updateValues(this.getValues(draft, ui));
        },
        getValues: (draft, ui) => ({
          xCount: draft.treadleCount,
          yCount: draft.weftCount,
          weftCount: draft.weftCount,
          warpCount: draft.warpCount,
          cellSize: ui.cellSize,
          scrollX: ui.scrollX,
          scrollY: ui.scrollY,
          yStepDistance: ui.yStepDistance,
        }),
        dirty: true,
        enabled: true,
      },
      warpColors: {
        renderer: new ColorRowRenderer(
          this.gl,
          this.shaders,
          true,
          false,
          'warpColors'
        ),
        updateValues: function (draft, ui) {
          this.renderer.updateValues(this.getValues(draft, ui));
        },
        getValues: (draft, ui) => ({
          xCount: draft.warpCount,
          yCount: 1,
          weftCount: draft.weftCount,
          warpCount: draft.warpCount,
          cellSize: ui.cellSize,
          scrollX: ui.scrollX,
          scrollY: ui.scrollY,
          xStepDistance: ui.xStepDistance,
        }),
        dirty: true,
        enabled: true,
      },
      weftColors: {
        renderer: new ColorRowRenderer(
          this.gl,
          this.shaders,
          false,
          true,
          'weftColors'
        ),
        updateValues: function (draft, ui) {
          this.renderer.updateValues(this.getValues(draft, ui));
        },
        getValues: (draft, ui) => ({
          xCount: 1,
          yCount: draft.weftCount,
          weftCount: draft.weftCount,
          warpCount: draft.warpCount,
          cellSize: ui.cellSize,
          scrollX: ui.scrollX,
          scrollY: ui.scrollY,
          yStepDistance: ui.yStepDistance,
        }),
        dirty: true,
        enabled: true,
      },
      weave: {
        renderer: new WeaveRenderer(this.gl, this.shaders, 'weave'),
        updateValues: function (draft, ui) {
          this.renderer.updateValues(this.getValues(draft, ui));
        },
        getValues: (draft, ui) => ({
          xCount: draft.warpCount,
          yCount: draft.weftCount,
          shaftCount: draft.shaftCount,
          treadleCount: draft.treadleCount,
          cellSize: ui.cellSize,
          scrollX: ui.scrollX,
          scrollY: ui.scrollY,
        }),
        dirty: true,
        enabled: true,
      },
    };
    this.names = Object.keys(this.renderers);

    let listOfRenderers = Object.values(this.renderers);

    // arggh
    window.setTimeout(() => {
      let render = () => {
        if (this.renderNextFrame) {
          for (let i = 0; i < listOfRenderers.length; i++) {
            let r = listOfRenderers[i];
            if (r.dirty && r.enabled) {
              r.renderer.render();
              r.dirty = false;
            }
          }
          this.renderNextFrame = false;
        }
        requestAnimationFrame(render);
      };
      requestAnimationFrame(render);
    });
  }

  setRendererPosition(draft, showTreadleOrder, showThreading) {
    let treadleCount = draft.treadleCount;
    let shaftCount = draft.shaftCount;
    this.showTreadleOrder = showTreadleOrder;
    this.showThreading = showThreading;

    let to = Number(showTreadleOrder);
    let th = Number(showThreading);

    this.renderers.tieup.enabled = showTreadleOrder && showThreading;
    this.renderers.tieup.renderer.setRendererPosition([3, 3]);

    this.renderers.threading.enabled = showThreading;
    this.renderers.threading.renderer.setRendererPosition([
      to * (treadleCount + 4),
      3,
    ]);

    this.renderers.treadleOrder.enabled = showTreadleOrder;
    this.renderers.treadleOrder.renderer.setRendererPosition([
      3,
      th * (shaftCount + 4),
    ]);

    this.renderers.weftColors.renderer.setRendererPosition([
      1,
      th * (shaftCount + 4),
    ]);
    this.renderers.weftColors.enabled = showTreadleOrder;

    this.renderers.warpColors.enabled = showThreading;
    this.renderers.warpColors.renderer.setRendererPosition([
      to * (treadleCount + 4),
      1,
    ]);
    this.renderers.weave.renderer.setRendererPosition([
      to * (treadleCount + 4),
      th * (shaftCount + 4),
    ]);

    this.clear();
  }

  isDifferent(draft, prevDraft, ...args) {
    return args.some((a) => {
      return draft[a] !== prevDraft[a];
    });
  }

  update(draft, ui, changes) {
    if (changes.paths.size) {
      this.updateTextures(draft, changes);

      for (let c of changes.paths) {
        let dependencies;
        if (c === 'clear' || this.dependencyGraph[c] === 'clear') {
          this.clearCanvas();
          dependencies = this.names;
        } else {
          dependencies = this.dependencyGraph[c];
        }
        if (dependencies !== undefined) {
          for (let j = 0; j < dependencies.length; j++) {
            let r = this.renderers[dependencies[j]];
            r.dirty = true;
            r.updateValues(draft, ui);
          }
        }
      }
      changes.clear();
    }
    this.startup = false;
  }

  createThreadingTexture(draft) {
    this.threadingTexture.delete();
    this.threadingTexture = textureUtil.create1DGridTexture(
      this.gl,
      draft.threading,
      draft.shaftCount,
      draft.warpCount
    );
    this.renderers.threading.renderer.setCellToggleTexture(
      this.threadingTexture
    );
    this.renderers.weave.renderer.setThreadingTexture(this.threadingTexture);
  }

  createTreadleOrderTexture(draft) {
    this.treadleOrderTexture.delete();
    this.treadleOrderTexture = textureUtil.create1DGridTexture(
      this.gl,
      draft.treadleOrder,
      draft.treadleCount,
      draft.weftCount
    );
    this.renderers.treadleOrder.renderer.setCellToggleTexture(
      this.treadleOrderTexture
    );
    this.renderers.weave.renderer.setTreadleOrderTexture(
      this.treadleOrderTexture
    );
  }

  createTieupTexture(draft) {
    this.tieupTexture.delete();
    this.tieupTexture = textureUtil.createGridTexture(
      this.gl,
      draft.tieup,
      draft.treadleCount,
      draft.shaftCount
    );
    this.renderers.tieup.renderer.setCellToggleTexture(this.tieupTexture);
    this.renderers.weave.renderer.setTieupTexture(this.tieupTexture);
  }

  createWarpTexture(draft) {
    this.warpTexture.delete();
    this.warpTexture = textureUtil.createColorTexture(
      this.gl,
      draft.warpColors,
      draft.warpColors.length,
      draft.yarn
    );
    this.renderers.warpColors.renderer.setColorTexture(this.warpTexture);
    this.renderers.weave.renderer.setWarpTexture(this.warpTexture);
  }

  createWeftTexture(draft) {
    this.weftTexture.delete();
    this.weftTexture = textureUtil.createColorTexture(
      this.gl,
      draft.weftColors,
      draft.weftColors.length,
      draft.yarn
    );
    this.renderers.weftColors.renderer.setColorTexture(this.weftTexture);
    this.renderers.weave.renderer.setWeftTexture(this.weftTexture);
  }

  getSpan(patches) {
    return patches
      .map((p) => p.path[1])
      .reduce((x, y) => [Math.min(x[0], y), Math.max(x[1], y)], [
        Number.MAX_VALUE,
        Number.MIN_VALUE,
      ]);
  }

  updateValuesInColorTexture(draft, changes, name, texture, height) {
    let patch = changes.patches[name];
    if (patch.every((p) => p.op === 'replace')) {
      let span = this.getSpan(patch);
      let data = draft[name].slice(span[0], span[1] + 1);
      textureUtil.update1DTexture(this.gl, texture, span[0], data, height);
    }
  }

  updateTextures(draft, changes) {
    if (this.startup) {
      this.createThreadingTexture(draft);
      this.createTieupTexture(draft);
      this.createTreadleOrderTexture(draft);
      this.createWarpTexture(draft);
      this.createWeftTexture(draft);
      return;
    }

    if (changes.patches['tieup'] !== undefined) {
      this.createTieupTexture(draft);
    }

    if (changes.patches['shaftCount'] !== undefined) {
      this.createThreadingTexture(draft);
      this.createTieupTexture(draft);
    }

    if (changes.patches['treadleCount'] !== undefined) {
      this.createTreadleOrderTexture(draft);
      this.createTieupTexture(draft);
    }

    if (changes.patches['warpCount'] !== undefined) {
      this.createThreadingTexture(draft);
      this.createWarpTexture(draft);
    }

    if (changes.patches['weftCount'] !== undefined) {
      this.createTreadleOrderTexture(draft);
      this.createWeftTexture(draft);
    }

    if (changes.patches['threading'] !== undefined) {
      let patch = changes.patches.threading;
      if (patch.every((p) => p.op === 'replace')) {
        let span = this.getSpan(patch);
        let data = draft.threading.slice(span[0], span[1] + 1);
        textureUtil.update1DTexture(
          this.gl,
          this.threadingTexture,
          span[0],
          data,
          draft.shaftCount
        );
      }
    } else {
      this.createThreadingTexture(draft);
    }

    if (changes.patches['treadleOrder'] !== undefined) {
      let patch = changes.patches.treadleOrder;
      if (patch.every((p) => p.op === 'replace')) {
        let span = this.getSpan(patch);
        let data = draft.treadleOrder.slice(span[0], span[1] + 1);
        textureUtil.update1DTexture(
          this.gl,
          this.treadleOrderTexture,
          span[0],
          data,
          draft.treadleCount
        );
      }
    } else {
      this.createTreadleOrderTexture(draft);
    }

    if (changes.patches['warpColors'] !== undefined) {
      let patch = changes.patches.warpColors;
      if (patch.every((p) => p.op === 'replace')) {
        let span = this.getSpan(patch);
        let data = draft.warpColors.slice(span[0], span[1] + 1);
        textureUtil.updateColorTexture(
          this.gl,
          this.warpTexture,
          span[0],
          data,
          draft.yarn
        );
      } else {
        this.createWarpTexture(draft);
      }
    }

    if (changes.patches['weftColors'] !== undefined) {
      let patch = changes.patches.weftColors;
      if (patch.every((p) => p.op === 'replace')) {
        let span = this.getSpan(patch);
        let data = draft.weftColors.slice(span[0], span[1] + 1);
        textureUtil.updateColorTexture(
          this.gl,
          this.weftTexture,
          span[0],
          data,
          draft.yarn
        );
      } else {
        this.createWeftTexture(draft);
      }
    }

    if (changes.patches['yarn'] !== undefined) {
      this.createWarpTexture(draft);
      this.createWeftTexture(draft);
    }
  }

  getRenderer(name) {
    return this.renderers[name].renderer;
  }

  getRendererByPos(pos) {
    for (let i = 0; i < this.names.length; i++) {
      let name = this.names[i];
      if (
        this.renderers[name].renderer.isWithinGrid(pos) &&
        this.renderers[name].enabled
      ) {
        return this.renderers[name].renderer;
      }
    }
    return undefined;
  }

  resizeCanvas() {
    this.width = this.gl.canvas.width;
    this.height = this.gl.canvas.height;
    this.gl.viewport(0, 0, this.width, this.height);
  }

  addEventListener(name, func) {
    this.scrollContainer.addEventListener(name, func);
  }

  render() {
    this.renderNextFrame = true;
  }

  clearCanvas() {
    let w = this.gl.canvas.width;
    let h = this.gl.canvas.height;
    this.gl.scissor(0, 0, w, h);
    this.gl.clear(this.gl.COLOR_BUFFER_BIT);
  }

  clear() {
    this.clearCanvas();
    Object.values(this.renderers).forEach((r) => (r.dirty = true));
    this.render();
  }
}
